import { EventEmitter, Inject, Injectable } from '@angular/core';
import { MapAddressService } from './map-address.service';
import { PosPrecartPickupService } from './pos-precart-pickup.service';
import { IPos } from '../../../../interfaces/pos-search.interface';
import { DeliveryFormStateService } from '../../../form-state/delivery-form-state.service';
import { IdentityConfirmationTypeEnum } from '../../../../models/cartDetails/identityConfirmationType.enum';
import { BehaviorSubject, fromEventPattern } from 'rxjs';
import { PosResources } from '../../../../external-interfaces/api-resources.service';
import { debounceTime, switchMap } from 'rxjs/operators';

declare const google: any;

export interface PosInterface {
  allPos: IPos.Marker[];
  activeItem: IPos.Marker | null;
  visibleDetails: boolean;
  providers: IPos.Provider[];
  selectedProvider: string;
  customLocalization: string;
}

@Injectable()
export class DeliveryModalService {

  public pos: PosInterface = {
    allPos: [],
    activeItem: null,
    visibleDetails: false,
    providers: [],
    selectedProvider: '',
    customLocalization: ''
  };

  private initMap = true;
  private fitMapToMarkersOn = false;
  private doNotLoadPOS = false;
  private posEventEmitter: EventEmitter<any> = new EventEmitter();
  public showError = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject('Endpoint') private endpoint: any,
    private posResources: PosResources,
    private mapAddressService: MapAddressService,
    private posPrecartPickupService: PosPrecartPickupService,
    private deliveryFormStateService: DeliveryFormStateService,
  ) {
  }

  public getPosById(posId: string) {
    return this.pos.allPos.find((pos: IPos.Marker) => pos.id === posId);
  }

  public showMapModal() {
    if (this.initMap) {
      this.bindMapEvents();
      this.mapAddressService.positionFromRegisteredAddress()
        .then(this.mapAddressService.positionFromBrowser.bind(this.mapAddressService))
        .then(this.mapAddressService.positionFromDefaultAddress.bind(this.mapAddressService))
        .catch(this.loadMapAndSetPos.bind(this));
    } else {
      this.searchPos();
    }
  }

  public seeOnMap(pos: IPos.Marker) {
    this.doNotLoadPOS = true;

    this.posPrecartPickupService.map.setOptions({
      zoom: 18,
      center: new google.maps.LatLng(pos.lat, pos.lng)
    });

    new google.maps.event.trigger(pos.markerMapHolder, 'click');

    this.posPrecartPickupService.markerClusterer.repaint();

    this.doNotLoadPOS = false;
  }

  public backToList() {
    this.pos.visibleDetails = false;
    this.resetActiveMarkerIcon();

    setTimeout(() => {
      this.pos.activeItem = null;
    }, 400);
  }

  public loadPosFromCustomLocalization(_customPosLocalization: string) {
    this.positionFromCustomAddress(_customPosLocalization)
      .then(this.loadMapAndSetPos.bind(this))
      .catch(() => {
      });
  }

  public posChangeSubscribe(_callback: Function) {
    return this.posEventEmitter.subscribe(_callback);
  }

  private positionFromCustomAddress(address: string) {
    return this.mapAddressService.positionFromCustomAddress(address);
  }

  private servicePosApi = {
    response: (posResponse: IPos.Root, endpointType: 'init' | 'next') => {
      this.pos.allPos = posResponse.pickupPoints;
      this.pos.providers = posResponse.providers;
      this.posEventEmitter.emit(posResponse.pickupPoints);
      this.setMarkers(endpointType);

      const shouldShowError = !(posResponse.pickupPoints && posResponse.pickupPoints.length);
      this.showError.next(shouldShowError);
    },
    error: (posResponse: any, type: string) => {
      console.warn(`ServicePosApi error - ${type} - ${posResponse}`);
      this.showError.next(true);
    }
  };

  private loadMapAndSetPos(value: {type?: string, location: google.maps.LatLng}) {
    this.posPrecartPickupService.map.setCenter(value.location);
    this.searchPos();
  }

  private getBoundsMap() {
    const boundsMap = this.posPrecartPickupService.map.getBounds();

    return {
      center: boundsMap.getCenter().toJSON(),
      northEast: boundsMap.getNorthEast().toJSON(),
      southWest: boundsMap.getSouthWest().toJSON()
    };
  }

  private setMarkers(endpointType: string) {
    this.posPrecartPickupService.markerClusterer.clearMarkers();
    this.addMarkersToMap();

    if (endpointType === 'init' && this.pos.allPos.length) {
      this.fitMapToMarkersOn = true;
      this.posPrecartPickupService.options.mapOptions.zoom = this.posPrecartPickupService.map.getZoom();
      this.fitMapToMarkersOn = false;
    }

    this.posPrecartPickupService.options.nextMapIsLoaded = true;
  }

  private addMarkersToMap() {
    const addListener = (marker: any, index: number) => {
      google.maps.event.addListener(marker, 'click', () => {
        this.resetActiveMarkerIcon();

        this.pos.activeItem = this.pos.allPos[index];
        this.pos.visibleDetails = true;

        this.pos.allPos[index].isActive = true;
        marker.setIcon({
          url: `${this.pos.allPos[index].iconActive ? this.pos.allPos[index].iconActive : window['themeResourcePath'] + '/images/maps/marker-default-active.png'}`,
          size: new google.maps.Size(74, 90)
        });
      });
    };

    const allMarkers = <google.maps.Marker[]>[];

    this.pos.allPos.forEach((item: IPos.Marker, index: number) => {
      let marker = new google.maps.Marker({
        position: new google.maps.LatLng(item.lat, item.lng),
        title: item.city + ' - ' + item.street,
        icon: {
          url: `${item.icon ? item.icon : window['themeResourcePath'] + '/images/maps/marker-default.png'}`,
          size: new google.maps.Size(68, 82)
        }
      });

      allMarkers.push(marker);

      item.markerMapHolder = marker;
      item.isActive = false;

      addListener(marker, index);
    });

    this.posPrecartPickupService.markerClusterer.addMarkers(allMarkers, true);
    this.posPrecartPickupService.markerClusterer.repaint();
  }

  private resetActiveMarkerIcon() {
    this.pos.allPos.forEach(item => {
      if (item.isActive) {
        item.isActive = false;
        item.markerMapHolder.setIcon({
          url: `${item.icon ? item.icon : window['themeResourcePath'] + '/images/maps/marker-default.png'}`,
          size: new google.maps.Size(68, 82)
        });
      }
    }, (reason: any) => {
      this.servicePosApi.error(reason, 'next');
    });
  }

  private bindMapEvents(): void {
    this.initMap = false;
    fromEventPattern(
      (handler) => {
        return this.posPrecartPickupService.map.addListener('dragend', handler);
      },
      (handler, listener) => {
        google.maps.event.removeListener(listener);
      }
    ).pipe(
      debounceTime(500),
      switchMap(() => {
        return this.findPosInArea();
      })
    ).subscribe((response: IPos.Root) => {
      this.servicePosApi.response(response, 'next');
    }, (reason: any) => {
      this.servicePosApi.error(reason, 'next');
    });

    fromEventPattern(
      (handler) => {
        return this.posPrecartPickupService.map.addListener('zoom_changed', handler);
      },
      (handler, listener) => {
        google.maps.event.removeListener(listener);
      }
    ).pipe(
      debounceTime(500),
      switchMap(() => {
        if (!this.fitMapToMarkersOn && !this.doNotLoadPOS) {
          this.posPrecartPickupService.options.mapOptions.zoom = this.posPrecartPickupService.map.getZoom();
          return this.findPosInArea();
        }

        return Promise.resolve();
      })
    ).subscribe((response: IPos.Root | undefined) => {
      if (response) {
        this.servicePosApi.response(response, 'next');
      }
    }, (reason: any) => {
      this.servicePosApi.error(reason, 'next');
    });
  }

  private getParams(): object {
    const params = this.getBoundsMap();

    Object.assign(params, {
      eIDSelected: this.deliveryFormStateService.outputState.identityConfirmationType === IdentityConfirmationTypeEnum.ONLINE || this.deliveryFormStateService.outputState.identityConfirmationType === IdentityConfirmationTypeEnum.M_OBYWATEL
    });

    if (this.pos.selectedProvider) {
      Object.assign(params, {
        types: this.pos.selectedProvider
      });
    }

    return params;
  }

  private findPosInArea(): Promise<any> {
    return this.posResources.findPosInArea(this.getParams()).toPromise();
  }

  public searchPos() {
    this.endpoint('pos.searchPos', this.getParams())
      .run()
      .then((response: IPos.Root) => this.servicePosApi.response(response, 'init'))
      .catch((reason: any) => this.servicePosApi.error(reason, 'init'));
  }
}
